import { useState, useEffect, useRef, useCallback } from 'react'
import { Eye, EyeOff, Trash2, Save, ExternalLink, Copy, CopyPlus, Tag, X, Play, Radio, ArrowRightLeft, Link2, Film, RefreshCw, RotateCw } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import { liveStatusLabel, liveStatusClass } from '../../../utils/liveStatus'
import ImagePicker from './ImagePicker'
import SimulcastConfigModal from './SimulcastConfigModal'

// Separate player component — unmounts/remounts cleanly via key prop
function VideoPlayer({ src, type, poster, onDuration }) {
  const containerRef = useRef(null)
  const playerRef = useRef(null)

  useEffect(() => {
    if (!document.getElementById('videojs-css')) {
      const link = document.createElement('link')
      link.id = 'videojs-css'
      link.rel = 'stylesheet'
      link.href = 'https://vjs.zencdn.net/8.10.0/video-js.css'
      document.head.appendChild(link)
    }
    if (!window.videojs && !document.getElementById('videojs-script')) {
      const script = document.createElement('script')
      script.id = 'videojs-script'
      script.src = 'https://vjs.zencdn.net/8.10.0/video.min.js'
      document.head.appendChild(script)
    }
  }, [])

  useEffect(() => {
    if (!src || !containerRef.current) return
    let player = null
    let checkInterval = null
    let timeout = null

    function init() {
      const container = containerRef.current
      if (!container) return
      const videoEl = document.createElement('video')
      videoEl.className = 'video-js vjs-big-play-centered'
      videoEl.setAttribute('controls', '')
      videoEl.setAttribute('preload', 'metadata')
      if (poster) videoEl.setAttribute('poster', poster)
      container.appendChild(videoEl)
      const isHLS = type === 'HLS' || type === 'MUX' || type === 'CF' || src.includes('.m3u8')
      player = window.videojs(videoEl, {
        fluid: true, responsive: true, controls: true, preload: 'metadata',
        poster: poster || '',
        sources: [{ src, type: isHLS ? 'application/x-mpegURL' : 'video/mp4' }]
      })
      player.on('loadedmetadata', () => {
        const dur = player.duration()
        if (dur && isFinite(dur) && onDuration) onDuration(Math.round(dur))
      })
      playerRef.current = player
    }

    if (window.videojs) {
      init()
    } else {
      checkInterval = setInterval(() => { if (window.videojs) { clearInterval(checkInterval); init() } }, 100)
      timeout = setTimeout(() => clearInterval(checkInterval), 10000)
    }

    return () => {
      if (checkInterval) clearInterval(checkInterval)
      if (timeout) clearTimeout(timeout)
      if (playerRef.current) { playerRef.current.dispose(); playerRef.current = null }
    }
  }, [src, type, poster, onDuration])

  return <div className="detail-player-container" ref={containerRef} />
}

export default function CatalogDetail({ item, allTags, onUpdate, onDelete, onDuplicate, setStatus, layout = 'single' }) {
  const { api } = useAuth()
  
  const [title, setTitle] = useState('')
  const [mediaUrl, setMediaUrl] = useState('')
  const [videoType, setVideoType] = useState('HLS')
  const originalHlsUrl = useRef('')  // Store HLS URL so we can restore when switching from YT
  const [quality, setQuality] = useState('FHD')
  const [duration, setDuration] = useState(0)
  const [shortDesc, setShortDesc] = useState('')
  const [longDesc, setLongDesc] = useState('')
  const [thumbnailUrl, setThumbnailUrl] = useState('')
  const [releaseDate, setReleaseDate] = useState('')
  const [releaseTime, setReleaseTime] = useState('')
  const [status, setItemStatus] = useState('draft')
  const [tags, setTags] = useState([])

  // Unified content library: live-event fields
  const [eventMode, setEventMode] = useState('ondemand')
  // Pre-live origin ('highlights' | 'ondemand') so a live event returns to the right mode.
  const [originEventMode, setOriginEventMode] = useState('ondemand')
  const [scheduledStart, setScheduledStart] = useState('')
  const [eventType, setEventType] = useState('completed')
  const [cfLiveInputId, setCfLiveInputId] = useState('')
  const [viloudChannelName, setViloudChannelName] = useState('')
  const [viloudChannelId, setViloudChannelId] = useState('')
  const [streamNotes, setStreamNotes] = useState('')
  const [durationMinutes, setDurationMinutes] = useState(120)
  const [includeInSchedule, setIncludeInSchedule] = useState(false)
  // LIVE 2.0 M2b: per-event live style. auto_start ON = hot HLS auto-goes-live (default);
  // auto_stop OFF = truly-live, a drop HOLDS until manual End (default). ON = re-stream auto-ends.
  const [autoStart, setAutoStart] = useState(true)
  const [autoStop, setAutoStop] = useState(false)
  // YouTube simulcast (tbtv-live-scheduler). Live state is server-authoritative (item.youtube_simulcast_mode
  // / _watch_url / _privacy, written back by the scheduler); ytPrivacy is the pre-provision picker.
  const [ytPrivacy] = useState(item?.youtube_privacy || 'unlisted')
  // Local, reactive simulcast state so the panel flips Create↔Remove immediately after an action
  // (the `item` prop is stale until the modal is reopened).
  const [simMode, setSimMode] = useState(item?.youtube_simulcast_mode || 'none')
  const [simWatchUrl, setSimWatchUrl] = useState(item?.youtube_watch_url || null)
  const [showSimModal, setShowSimModal] = useState(false)
  const [simModalMode, setSimModalMode] = useState('create') // 'create' | 'edit'
  const [simStatus, setSimStatus] = useState(null) // on-demand status payload from /simulcast/status
  const [simStatusLoading, setSimStatusLoading] = useState(false)
  const [liveInputs, setLiveInputs] = useState([])
  const [tbtvChannels, setTbtvChannels] = useState([])
  const [switchingMode, setSwitchingMode] = useState(false)
  const [duplicating, setDuplicating] = useState(false)
  const [copiedLink, setCopiedLink] = useState(false)

  // Mux stream picker
  const [muxAssets, setMuxAssets] = useState([])
  const [muxLoading, setMuxLoading] = useState(false)
  const [showMuxPicker, setShowMuxPicker] = useState(false)
  const [muxSearch, setMuxSearch] = useState('')
  const [muxTagFilter, setMuxTagFilter] = useState('')
  const [muxTaggedIds, setMuxTaggedIds] = useState(null) // null = no filter
  const [muxAssetTags, setMuxAssetTags] = useState({}) // tbtv_id -> tags[]

  // CF Stream picker
  const [cfStreams, setCfStreams] = useState([])
  const [cfLoading, setCfLoading] = useState(false)
  const [showCfPicker, setShowCfPicker] = useState(false)
  const [cfSearch, setCfSearch] = useState('')
  const [cfTagFilter, setCfTagFilter] = useState('')
  const [cfTaggedUids, setCfTaggedUids] = useState(null) // null = no filter
  const [cfStreamTags, setCfStreamTags] = useState({}) // uid -> tags[]
  const [tagInput, setTagInput] = useState('')
  const [showTagSuggestions, setShowTagSuggestions] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savingTags, setSavingTags] = useState(false)
  // Sport Profiles Phase 5.1 #2: per-video profile override.
  // profileKey drives which sport-profile prompts + vocab the AI pipeline
  // uses. profileKeySource ('auto' | 'manual') reflects whether the value
  // came from the detect helper (Phase 5.1 #3) or an editor override here.
  // savedProfileKey tracks the last-persisted value so the compact row
  // can flip its Save button to "dirty" the moment the dropdown changes,
  // without waiting for a full Catalog save.
  const [profileKey, setProfileKey] = useState('hockey')
  const [savedProfileKey, setSavedProfileKey] = useState('hockey')
  const [profileKeySource, setProfileKeySource] = useState('auto')
  const [availableProfiles, setAvailableProfiles] = useState([])
  const [savingProfile, setSavingProfile] = useState(false)
  const [showGoLiveModal, setShowGoLiveModal] = useState(false)
  const [controlling, setControlling] = useState('')
  const [showToVodModal, setShowToVodModal] = useState(false)
  const [toVodActive, setToVodActive] = useState(true)
  const [toVodTags, setToVodTags] = useState('')
  const [movingToVod, setMovingToVod] = useState(false)

  useEffect(() => {
    if (!item) return
    setTitle(item.title || '')
    setMediaUrl(item.media_url || '')
    setVideoType(item.video_type || 'HLS')
    // Store HLS URL for restore when switching away from YT
    if (item.video_type !== 'YT' && item.media_url) {
      originalHlsUrl.current = item.media_url
    }
    setQuality(item.quality || 'FHD')
    setDuration(item.duration || 0)
    setShortDesc(item.short_description || '')
    setLongDesc(item.long_description || '')
    setThumbnailUrl(item.thumbnail_url || '')
    setReleaseDate(item.release_date || '')
    setReleaseTime(item.release_time || '')
    setItemStatus(item.status || 'draft')
    setTags(item.tags || [])
    // Live-event fields
    setEventMode(item.event_mode || 'ondemand')
    // Origin = stored origin (if live) else the current non-live mode; default ondemand.
    setOriginEventMode(item.origin_event_mode || (item.event_mode && item.event_mode !== 'live' ? item.event_mode : 'ondemand'))
    setEventType(item.event_type || 'completed')
    setCfLiveInputId(item.cf_live_input_id || '')
    setViloudChannelName(item.viloud_channel_name || '')
    setViloudChannelId(item.viloud_channel_id || '')
    setStreamNotes(item.stream_notes || '')
    setDurationMinutes(item.duration_minutes || 120)
    // Sport Profile — server returns defaults ('hockey' / 'auto') when the row is untouched.
    setProfileKey(item.profile_key || 'hockey')
    setSavedProfileKey(item.profile_key || 'hockey')
    setProfileKeySource(item.profile_key_source || 'auto')
    setIncludeInSchedule(item.include_in_schedule ? true : false)
    setAutoStart(item.auto_start == null ? true : !!item.auto_start)
    setAutoStop(item.auto_stop == null ? false : !!item.auto_stop)
    // Convert ISO to datetime-local
    if (item.scheduled_start) {
      const dt = new Date(item.scheduled_start)
      const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
      setScheduledStart(local)
    } else {
      setScheduledStart('')
    }
  }, [item])

  // Load live inputs and channels for dropdowns
  useEffect(() => {
    async function loadLiveData() {
      try {
        const [inputsRes, channelsRes] = await Promise.all([
          api('/live-inputs').catch(() => ({})),
          api('/channels').catch(() => ({}))
        ])
        setLiveInputs(inputsRes.liveInputs || [])
        setTbtvChannels(channelsRes.channels || [])
      } catch { /* non-critical */ }
    }
    loadLiveData()
  }, [])

  // Load enabled sport profiles for the profile-override dropdown (Phase 5.1 #2).
  // Only enabled ones — a disabled profile shouldn't be selectable. If the fetch
  // fails, the dropdown falls back to just the current profile so the editor can
  // still see what's set, they just can't change it (which matches "gracefully
  // degrade" — profile is a low-frequency edit, not blocking).
  useEffect(() => {
    async function loadProfiles() {
      try {
        const res = await api('/highlights/profiles?enabled_only=true')
        setAvailableProfiles(Array.isArray(res?.profiles) ? res.profiles : [])
      } catch { /* non-critical */ }
    }
    loadProfiles()
  }, [])

  // Auto-load simulcast status once when an active simulcast is present (on-demand; no polling).
  useEffect(() => {
    if (simMode === 'simulcast' && !simStatus && item?.tbtv_id) refreshSimStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simMode, item?.tbtv_id])

  const handleDetectedDuration = useCallback((detectedSeconds) => {
    if (detectedSeconds > 0) setDuration(detectedSeconds)
  }, [])

  async function loadMuxAssets() {
    setMuxLoading(true)
    try {
      const data = await api('/mux/assets?limit=100')
      const list = data.data || data || []
      setMuxAssets(list)
      // Load tags for assets that have tbtv_ids
      const tagMap = {}
      await Promise.all(list.map(async a => {
        const tbtvId = a.meta?.external_id
        if (tbtvId) {
          try {
            const d = await api(`/tags/${tbtvId}`)
            tagMap[tbtvId] = d.tags || []
          } catch { tagMap[tbtvId] = [] }
        }
      }))
      setMuxAssetTags(tagMap)
    } catch {
      setMuxAssets([])
    }
    setMuxLoading(false)
  }

  async function loadCfStreams() {
    setCfLoading(true)
    try {
      const data = await api('/videos?per_page=100')
      const list = Array.isArray(data) ? data
        : data?.result ? data.result
        : data?.data?.result ? data.data.result
        : data?.videos ? data.videos
        : []
      setCfStreams(list)
      // Load tags for CF streams that have content records
      const tagMap = {}
      await Promise.all(list.map(async s => {
        try {
          const resolved = await api('/content/resolve', {
            method: 'POST',
            body: JSON.stringify({ stream_uid: s.uid, title: s.meta?.name || s.uid })
          })
          if (resolved.tbtv_id) {
            const d = await api(`/tags/${resolved.tbtv_id}`)
            tagMap[s.uid] = d.tags || []
          }
        } catch { /* no content record yet */ }
      }))
      setCfStreamTags(tagMap)
    } catch {
      setCfStreams([])
    }
    setCfLoading(false)
  }

  // Tag filter for Mux picker
  useEffect(() => {
    if (!muxTagFilter) { setMuxTaggedIds(null); return }
    api(`/tags/filter?tag=${encodeURIComponent(muxTagFilter)}`)
      .then(d => {
        const tbtvIds = new Set((d.items || d.results || []).map(r => r.tbtv_id).filter(Boolean))
        const archiveKeys = new Set((d.archive_keys || []).filter(Boolean))
        setMuxTaggedIds({ tbtvIds, archiveKeys })
      })
      .catch(() => setMuxTaggedIds({ tbtvIds: new Set(), archiveKeys: new Set() }))
  }, [muxTagFilter, api])

  // Tag filter for CF picker
  useEffect(() => {
    if (!cfTagFilter) { setCfTaggedUids(null); return }
    api(`/tags/filter?tag=${encodeURIComponent(cfTagFilter)}`)
      .then(d => setCfTaggedUids(new Set((d.stream_uids || []).filter(Boolean))))
      .catch(() => setCfTaggedUids(new Set()))
  }, [cfTagFilter, api])

  // Single stream-aware End (End = end + auto-hand-off to the next event). If the CF stream is still
  // hot, the coordinator returns needsConfirm and we ask a second, stronger confirmation before forcing.
  async function handleEndEvent(force = false) {
    if (!force && !window.confirm('End this live event?\n\nThe next scheduled event on this input/channel will then follow the normal live logic (starting-soon → live when its stream is hot).')) return
    setControlling('end'); setStatus('')
    try {
      const r = await api(`/live/${item.tbtv_id}/end`, { method: 'POST', body: JSON.stringify({ reason: 'operator end', force }) })
      const coord = r?.coordinator
      if (coord?.needsConfirm) {
        setControlling('')
        if (window.confirm('⚠ The live stream is STILL ACTIVE.\n\nEnd this event anyway while it is still streaming? (Rare — normally the stream has already stopped before you End.)')) {
          return handleEndEvent(true)
        }
        return
      }
      setStatus(`✅ Event ended (${coord?.broadcast || 'ended'}). The next scheduled event will come up as starting-soon, then live when its stream is hot.`)
      onUpdate()
      // Offer the post-end publish flow (move the recording to on-demand).
      setToVodTags((tags || []).join(', '))
      setToVodActive(true)
      setShowToVodModal(true)
    } catch (e) {
      setStatus(overlapMessage(e) || `Error: ${e.message}`)
    }
    setControlling('')
  }

  // Manual Go Live (for Auto-Start OFF events): promote a hot-but-waiting event to live now.
  async function handleGoLiveNow() {
    if (!window.confirm('Go live now?\n\nThis event will switch to LIVE immediately (once the coordinator sees the stream).')) return
    setControlling('golive'); setStatus('')
    try {
      const r = await api(`/live/${item.tbtv_id}/golive`, { method: 'POST', body: JSON.stringify({ reason: 'operator go-live' }) })
      const coord = r?.coordinator
      setStatus(`✅ Going live (${coord?.broadcast || 'live'}).`)
      onUpdate()
    } catch (e) {
      setStatus(overlapMessage(e) || `Error: ${e.message}`)
    }
    setControlling('')
  }

  // YouTube simulcast — created via the SimulcastConfigModal (in-vader ytProvision). This callback flips the
  // panel to the status/manage view immediately after a successful create.
  function handleSimulcastCreated(r) {
    if (r?.edited) {
      // In-place edit of an active broadcast — just refresh the status view.
      const warn = (r.warnings && r.warnings.length) ? ` (warnings: ${r.warnings.join('; ')})` : ''
      setStatus(`✅ Simulcast updated.${warn}`)
      setTimeout(() => { refreshSimStatus() }, 300)
      return
    }
    setSimMode('simulcast'); setSimWatchUrl(r?.watchUrl || null)
    setStatus(`✅ Simulcasting to YouTube. ${r?.watchUrl || ''}`)
    onUpdate()
    // pull fresh status so the manage view is populated
    setTimeout(() => { refreshSimStatus() }, 300)
  }
  // On-demand status/visibility: lifecycle, privacy, drift, Studio link. Also opportunistically unlists a
  // completed replay when unlist-replay is set (server-side).
  async function refreshSimStatus() {
    setSimStatusLoading(true)
    try {
      const r = await api(`/live/${item.tbtv_id}/simulcast/status`)
      if (r?.ok) setSimStatus(r.simulcast || null)
      else setStatus(`Status: ${r?.message || r?.error || 'failed'}`)
    } catch (e) { setStatus(`Error: ${e.message}`) }
    setSimStatusLoading(false)
  }
  // Manual resync: push the catalog's current title/start/description to the active YouTube broadcast.
  async function handleSyncSimulcast() {
    setControlling('simsync'); setStatus('')
    try {
      const r = await api(`/live/${item.tbtv_id}/simulcast/sync`, { method: 'POST' })
      if (r?.ok) { setStatus(r.synced ? `✅ Synced to YouTube (${(r.fields || []).join(', ')}).` : 'Nothing to sync.'); refreshSimStatus() }
      else setStatus(`Sync: ${r?.message || r?.error || 'failed'}`)
    } catch (e) { setStatus(`Error: ${e.message}`) }
    setControlling('')
  }
  async function handleDisableSimulcast() {
    if (!window.confirm('Remove the YouTube simulcast broadcast for this event? (The reusable input binding stays; only this event’s broadcast is deleted.)')) return
    setControlling('simulcast'); setStatus('')
    try {
      const r = await api(`/live/${item.tbtv_id}/simulcast`, { method: 'DELETE' })
      if (r?.ok) { setSimMode('none'); setSimWatchUrl(null); setSimStatus(null); setStatus('YouTube simulcast removed.'); onUpdate() }
      else setStatus(`YouTube simulcast: ${r?.message || r?.error || 'failed'}`)
    } catch (e) { setStatus(`Error: ${e.message}`) }
    setControlling('')
  }

  // Post-end publish: move the ended live event back to its origin mode (On-Demand, or Highlights if
  // it came from a highlight) — which also drops it from the Live Schedule via include_in_schedule=0 —
  // optionally set Active, and apply tags. Keeps Live Schedule and the catalog in sync (same D1).
  async function handleMoveToVod() {
    const target = originEventMode === 'highlights' ? 'highlights' : 'ondemand'
    const targetLabel = target === 'highlights' ? 'Highlights' : 'On-Demand'
    setMovingToVod(true); setStatus('')
    try {
      await api(`/vod/${item.tbtv_id}/switch-mode`, { method: 'PATCH', body: JSON.stringify({ event_mode: target }) })
      if (toVodActive) await api(`/vod/${item.tbtv_id}`, { method: 'PATCH', body: JSON.stringify({ status: 'active' }) })
      const newTags = toVodTags.split(',').map(t => t.trim()).filter(Boolean)
      await api(`/tags/${item.tbtv_id}`, { method: 'PUT', body: JSON.stringify({ tags: newTags }) })
      setEventMode(target); setEventType('completed'); setIncludeInSchedule(false)
      if (toVodActive) setItemStatus('active')
      setTags(newTags)
      setShowToVodModal(false)
      setStatus(`✅ Moved to ${targetLabel} (removed from Live Schedule)` + (toVodActive ? ', set Active' : '') + '.')
      onUpdate()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setMovingToVod(false)
  }

  // Sport Profiles Phase 5.1 #2: save ONLY the profile_key via the standard
  // /vod PATCH endpoint. Separate from the main "Save Changes" so an editor
  // can override the profile without touching other fields (and vice-versa).
  // Server stamps profile_key_source='manual' when the value differs from
  // the currently-stored one, so the badge flips to MANUAL after save.
  async function handleSaveProfile() {
    if (!item?.tbtv_id || savingProfile || profileKey === savedProfileKey) return
    setSavingProfile(true)
    try {
      await api(`/vod/${item.tbtv_id}`, {
        method: 'PATCH',
        body: JSON.stringify({ profile_key: profileKey }),
      })
      setSavedProfileKey(profileKey)
      setProfileKeySource('manual')  // server just stamped it; mirror locally
      setStatus('✅ Sport profile saved')
      onUpdate()
    } catch (e) {
      setStatus(`Error saving profile: ${e.message}`)
    } finally {
      setSavingProfile(false)
    }
  }

  async function handleSave() {
    setSaving(true)
    try {
      const isoScheduledStart = scheduledStart ? new Date(scheduledStart).toISOString() : null
      await api(`/vod/${item.tbtv_id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title,
          media_url: mediaUrl,
          video_type: videoType,
          quality,
          duration: parseInt(duration) || 0,
          short_description: shortDesc,
          long_description: longDesc,
          thumbnail_url: thumbnailUrl,
          release_date: releaseDate || null,
          release_time: releaseTime || null,
          status,
          // Live-event fields
          event_mode: eventMode,
          scheduled_start: isoScheduledStart,
          event_type: eventType,
          cf_live_input_id: cfLiveInputId || null,
          viloud_channel_name: viloudChannelName || null,
          viloud_channel_id: viloudChannelId || null,
          stream_notes: streamNotes || null,
          duration_minutes: parseInt(durationMinutes) || 120,
          include_in_schedule: includeInSchedule ? 1 : 0,
          auto_start: autoStart ? 1 : 0,
          auto_stop: autoStop ? 1 : 0,
          // Sport Profiles Phase 5.1 #2: server stamps source='manual' when this
          // differs from the current value; unchanged → no write to profile_key_source.
          profile_key: profileKey,
        })
      })
      setStatus('✅ Saved')
      onUpdate()
    } catch (e) {
      setStatus(overlapMessage(e) || `Error: ${e.message}`)
    }
    setSaving(false)
  }

  async function handleDuplicate() {
    if (!confirm(`Duplicate "${item.title}" as a draft copy?`)) return
    setDuplicating(true)
    try {
      const res = await api(`/vod/${item.tbtv_id}/duplicate`, { method: 'POST' })
      setStatus(`✅ Duplicated as "${res.title}" (draft)`)
      if (onDuplicate) onDuplicate(res.tbtv_id)
      else onUpdate()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setDuplicating(false)
  }

  function handleSwitchMode() {
    // Live → return to the pre-live origin (highlights or ondemand). Non-live → go live.
    const newMode = eventMode === 'live' ? (originEventMode === 'highlights' ? 'highlights' : 'ondemand') : 'live'
    if (newMode === 'live') {
      // Open the Go-Live modal to collect the required live settings first
      // (fixes the old chicken-and-egg: the live form only showed AFTER switching).
      if (!scheduledStart) {
        const d = new Date(Date.now() + 60 * 60000) // default: 1h from now
        setScheduledStart(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16))
      }
      // Prepopulate the live duration from the item's known video length (seconds → minutes).
      const knownSecs = Number(item?.duration) || 0
      if (knownSecs > 0) setDurationMinutes(Math.max(1, Math.round(knownSecs / 60)))
      setShowGoLiveModal(true)
      return
    }
    doSwitchMode(newMode)
  }

  // Friendly message for the live-input overlap guard (HTTP 409 from the backend).
  function overlapMessage(e) {
    const c = e?.body?.conflict
    if (e?.body?.error === 'live_schedule_overlap' && c) {
      const fmt = (s) => { try { return new Date(s).toLocaleString() } catch { return s } }
      const on = c.reason === 'channel' ? 'the same channel'
        : c.reason === 'input+channel' ? 'the same live input and channel'
        : 'the same live input'
      const fix = c.reason === 'channel' ? 'change the start time, duration, or channel'
        : 'change the start time, duration, live input, or channel'
      return `⛔ Live schedule overlap: this event's window conflicts with "${c.title}" (${fmt(c.scheduledStart)} → ${fmt(c.scheduledEnd)}) on ${on}. Two live events can't overlap there — ${fix}.`
    }
    return null
  }

  async function doSwitchMode(newMode) {
    if (newMode === 'live' && (!scheduledStart || !cfLiveInputId)) {
      setStatus('Error: Scheduled Start and CF Live Input are required to go live')
      return
    }
    setSwitchingMode(true)
    try {
      const isoScheduledStart = scheduledStart ? new Date(scheduledStart).toISOString() : null
      await api(`/vod/${item.tbtv_id}/switch-mode`, {
        method: 'PATCH',
        body: JSON.stringify({
          event_mode: newMode,
          scheduled_start: isoScheduledStart,
          cf_live_input_id: cfLiveInputId || null,
          viloud_channel_name: viloudChannelName || null,
          viloud_channel_id: viloudChannelId || null,
          duration_minutes: durationMinutes || null,
          auto_start: autoStart ? 1 : 0,
          auto_stop: autoStop ? 1 : 0,
        })
      })
      setEventMode(newMode)
      setEventType(newMode === 'live' ? (eventType || 'upcoming') : 'completed')
      setIncludeInSchedule(newMode === 'live')
      setShowGoLiveModal(false)
      const modeLabel = newMode === 'live' ? 'Live' : newMode === 'highlights' ? 'Highlights' : 'On-Demand'
      setStatus(`✅ Switched to ${modeLabel}`)
      onUpdate()
    } catch (e) {
      setStatus(overlapMessage(e) || `Error: ${e.message}`)
    }
    setSwitchingMode(false)
  }

  function handleVideoTypeChange(newType) {
    const prevType = videoType
    // Use first portion of title as search hint for library pickers
    const searchHint = (title || '').substring(0, 20).trim()

    if (newType === 'YT') {
      if (!item?.youtube_id) {
        setStatus('⚠ This item has no linked YouTube video — cannot switch to YouTube type')
        return
      }
      if (prevType !== 'YT' && mediaUrl) {
        originalHlsUrl.current = mediaUrl
      }
      setVideoType(newType)
      setMediaUrl(`https://www.youtube.com/embed/${item.youtube_id}?autoplay=1&rel=0`)
    } else if (newType === 'MUX') {
      // Open Mux picker with title search pre-populated
      setVideoType(newType)
      setMuxSearch(searchHint)
      setShowMuxPicker(true)
      if (!muxAssets.length) loadMuxAssets()
    } else if (newType === 'CF') {
      // Open CF picker with title search pre-populated
      setVideoType(newType)
      setCfSearch(searchHint)
      setShowCfPicker(true)
      if (!cfStreams.length) loadCfStreams()
    } else if (prevType === 'YT') {
      // Switching FROM YouTube — restore saved HLS URL
      if (!originalHlsUrl.current) {
        setStatus('⚠ No CF/Mux HLS URL available — set a Media URL manually after switching')
      }
      setVideoType(newType)
      if (originalHlsUrl.current) {
        setMediaUrl(originalHlsUrl.current)
      }
    } else {
      // MP4↔HLS: leave URL as-is
      setVideoType(newType)
    }
  }

  function handleTbtvChannelSelect(viloudId) {
    if (!viloudId) {
      setViloudChannelId('')
      setViloudChannelName('')
      return
    }
    const ch = tbtvChannels.find(c => c.viloud_id === viloudId)
    if (ch) {
      setViloudChannelId(ch.viloud_id || '')
      setViloudChannelName(ch.name || '')
      if (ch.cf_live_input_id) setCfLiveInputId(ch.cf_live_input_id)
    }
  }

  function getShareUrl() {
    // Unified permalink — one URL that works for live AND on-demand
    return `https://watch.tenband.tv/watch/${item.tbtv_id}`
  }

  function copyShareLink() {
    const url = getShareUrl()
    navigator.clipboard.writeText(url).then(() => {
      setCopiedLink(true)
      setTimeout(() => setCopiedLink(false), 2000)
    })
  }

  async function handleToggleStatus() {
    const newStatus = status === 'active' ? 'draft' : 'active'
    setSaving(true)
    try {
      await api(`/vod/${item.tbtv_id}`, { method: 'PATCH', body: JSON.stringify({ status: newStatus }) })
      setItemStatus(newStatus)
      setStatus(`✅ Status changed to ${newStatus}`)
      onUpdate()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setSaving(false)
  }

  function addTag(tagName) {
    const clean = tagName.trim().toLowerCase()
    if (!clean || tags.includes(clean)) return
    setTags(prev => [...prev, clean])
    setTagInput('')
    setShowTagSuggestions(false)
  }

  function removeTag(tagName) {
    setTags(prev => prev.filter(t => t !== tagName))
  }

  function handleTagKeyDown(e) {
    if (e.key === 'Enter') { e.preventDefault(); addTag(tagInput) }
    if (e.key === 'Backspace' && !tagInput && tags.length > 0) removeTag(tags[tags.length - 1])
  }

  async function handleSaveTags() {
    setSavingTags(true)
    try {
      await api(`/tags/${item.tbtv_id}`, { method: 'PUT', body: JSON.stringify({ tags }) })
      setStatus('✅ Tags saved')
      onUpdate()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setSavingTags(false)
  }

  const combinedTags = [...new Set([...allTags, ...tags])]
  const filteredSuggestions = tagInput.length > 0
    ? combinedTags.filter(t => t.includes(tagInput.toLowerCase()) && !tags.includes(t))
    : []

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text)
    setStatus('✅ Copied')
  }

  function formatDuration(secs) {
    if (!secs || secs <= 0) return '—'
    const h = Math.floor(secs / 3600)
    const m = Math.floor((secs % 3600) / 60)
    const s = secs % 60
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  if (!item) return null

  const playerBlock = (
    <>
      {mediaUrl ? (
        <div className="detail-player-wrap">
          {videoType === 'YT' ? (
            <div className="detail-yt-player">
              <iframe
                src={mediaUrl}
                title={title || 'YouTube Video'}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <VideoPlayer
              key={item.tbtv_id}
              src={mediaUrl}
              type={videoType}
              poster={thumbnailUrl}
              onDuration={handleDetectedDuration}
            />
          )}
          <div className="detail-player-info">
            <span className={`player-badge ${videoType === 'YT' ? 'player-badge-yt' : ''}`}>{videoType}</span>
            <span className="player-badge">{quality}</span>
            <span className="player-duration">{formatDuration(parseInt(duration) || 0)}</span>
            <a href={mediaUrl} target="_blank" rel="noopener noreferrer" className="player-external">
              <ExternalLink size={12} /> {videoType === 'YT' ? 'Open on YouTube' : 'Open Direct'}
            </a>
          </div>
        </div>
      ) : thumbnailUrl ? (
        <div className="detail-preview">
          <img src={thumbnailUrl} alt="" />
          <div className="preview-no-media">No media URL set</div>
        </div>
      ) : null}

      <div className="detail-section detail-meta">
        <h3>Metadata</h3>
        <div className="meta-grid">
          <div className="meta-item"><label>TBTV ID</label><code>{item.tbtv_id}</code></div>
          <div className="meta-item"><label>Created</label><span>{item.created_at ? new Date(item.created_at).toLocaleString() : '—'}</span></div>
          <div className="meta-item"><label>Updated</label><span>{item.updated_at ? new Date(item.updated_at).toLocaleString() : '—'}</span></div>
          <div className="meta-item"><label>Date Added</label><span>{item.date_added ? new Date(item.date_added).toLocaleString() : '—'}</span></div>
        </div>
      </div>
    </>
  )

  const editBlock = (
    <>
      {(item?.event_mode === 'highlights' || eventMode === 'highlights') && (
        <HighlightLinkedReelSection
          tbtvId={item.tbtv_id}
          api={api}
          onRelinked={() => onSaved && onSaved()}
        />
      )}

      <div className="detail-section">
        <h3>Basic Information</h3>
        
        <div className="form-row">
          <label>Title</label>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Video title" />
        </div>

        <div className="form-row">
          <label>Media URL</label>
          <div className="input-with-actions">
            <input type="text" value={mediaUrl} onChange={e => setMediaUrl(e.target.value)} placeholder="HLS or MP4 URL" />
            <div className="media-action-btns">
              <button onClick={() => copyToClipboard(mediaUrl)} title="Copy"><Copy size={14} /></button>
              {mediaUrl && (
                <a href={mediaUrl} target="_blank" rel="noopener noreferrer" title="Open"><ExternalLink size={14} /></a>
              )}
              {mediaUrl !== (item?.media_url || '') && (
                <button
                  onClick={() => { setMediaUrl(item?.media_url || ''); setDuration(item?.duration || 0) }}
                  title="Revert to saved URL"
                  style={{ fontSize: 11, whiteSpace: 'nowrap', color: '#ef4444' }}
                >
                  ↩ Revert
                </button>
              )}
              <button
                onClick={() => { setMuxSearch((title || '').substring(0, 20).trim()); setShowMuxPicker(true); if (!muxAssets.length) loadMuxAssets() }}
                title="Browse Mux assets"
                style={{ fontSize: 11, whiteSpace: 'nowrap' }}
              >
                ▸ Mux
              </button>
              <button
                onClick={() => { setCfSearch((title || '').substring(0, 20).trim()); setShowCfPicker(true); if (!cfStreams.length) loadCfStreams() }}
                title="Browse Cloudflare Stream videos"
                style={{ fontSize: 11, whiteSpace: 'nowrap' }}
              >
                ▸ CF Stream
              </button>
            </div>
          </div>
        </div>

        <div className="form-row-grid">
          <div className="form-row">
            <label>Video Type</label>
            <select value={videoType} onChange={e => handleVideoTypeChange(e.target.value)}>
              <option value="MUX">MUX</option>
              <option value="CF">CF</option>
              <option value="MP4">MP4</option>
              <option value="HLS">HLS (legacy)</option>
              <option value="YT">YouTube (web only)</option>
            </select>
          </div>
          <div className="form-row">
            <label>Quality</label>
            <select value={quality} onChange={e => setQuality(e.target.value)}>
              <option value="SD">SD (480p)</option>
              <option value="HD">HD (720p)</option>
              <option value="FHD">FHD (1080p)</option>
              <option value="UHD">UHD (4K)</option>
            </select>
          </div>
        </div>

        <div className="form-row-grid">
          <div className="form-row">
            <label>Duration (seconds)</label>
            <div className="duration-input">
              <input type="number" value={duration} onChange={e => setDuration(e.target.value)} min="0" />
              <span className="duration-display">{formatDuration(parseInt(duration) || 0)}</span>
            </div>
          </div>
          <div className="form-row">
            <label>Release Date</label>
            <input type="date" value={releaseDate} onChange={e => setReleaseDate(e.target.value)} />
          </div>
        </div>

        {/* Release Time — this is the key field for same-date ordering across all apps */}
        <div className="form-row-grid">
          <div className="form-row">
            <label title="Controls the order of videos on the same release date across Roku, Fire TV, and Web. Later times appear first (most recent first).">
              Release Time
              <span style={{ marginLeft: 5, fontSize: '0.75em', color: '#888' }}>orders same-date videos ↑</span>
            </label>
            <input
              type="time"
              value={releaseTime}
              onChange={e => setReleaseTime(e.target.value)}
              style={{ maxWidth: 140 }}
            />
          </div>
          <div className="form-row">
            <label>Status</label>
            <select value={status} onChange={e => setItemStatus(e.target.value)}>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <label>Thumbnail URL</label>
          <ImagePicker value={thumbnailUrl} onChange={setThumbnailUrl} placeholder="Paste URL or browse Cloudflare Images..." />
        </div>

        <div className="form-row">
          <label>Short Description</label>
          <textarea value={shortDesc} onChange={e => setShortDesc(e.target.value)} placeholder="Brief description for listings" rows={2} />
        </div>

        <div className="form-row">
          <label>Long Description</label>
          <textarea value={longDesc} onChange={e => setLongDesc(e.target.value)} placeholder="Detailed description" rows={4} />
        </div>

        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={14} /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Live Event Settings (visible for all items, fields only editable in live mode) */}
      {eventMode === 'live' && (
        <div className="detail-section detail-section-live">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Radio size={16} /> Live Event Settings</span>
            {/* Live status is driven by the v2 coordinator lifecycle (not the manual Event Type). */}
            <span className={`event-mode-badge live ${liveStatusClass(item?.live_v2_status, item?.event_type)}`} title="Live status — driven by the live coordinator, updates through the lifecycle">
              {liveStatusLabel(item?.live_v2_status, item?.event_type)}
            </span>
          </h3>

          {/* Operator control (LIVE 2.0 M2): single stream-aware End = end + auto-hand-off to next.
              End is only actionable once the event has gone on air (actual_start set) — an event that
              hasn't started yet has nothing to end. The stream has usually already stopped by End time;
              End is the authoritative "this event is genuinely over" signal (vs a transient glitch,
              which the interrupted/drop-grace states absorb). */}
          {(() => { const hasBeenLive = !!(item?.actual_start) || ['live', 'interrupted', 'drop_grace'].includes(item?.live_v2_status); return (
          <div className="live-operator-control" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
            {(!autoStart || item?.live_v2_status === 'needs_operator') && !hasBeenLive && (
              <button className="btn-primary" disabled={!!controlling} onClick={handleGoLiveNow} title={item?.live_v2_status === 'needs_operator'
                ? 'Needs operator: two events share this input — go live now to bind this one'
                : 'Auto-Start is off for this event — promote it to live now (once the stream is hot)'}>
                {controlling === 'golive' ? 'Going live…' : '▶ Go Live Now'}
              </button>
            )}
            <button className="btn-danger" disabled={!!controlling || !hasBeenLive} onClick={() => handleEndEvent(false)} title={hasBeenLive ? 'End this live event and hand off to the next scheduled event' : "This event hasn't gone live yet — nothing to end"}>
              {controlling === 'end' ? 'Ending…' : '■ End Event'}
            </button>
            <button className="btn-secondary" disabled={!!controlling} onClick={() => { setToVodTags((tags || []).join(', ')); setToVodActive(true); setShowToVodModal(true) }} title={`Publish the recording to ${originEventMode === 'highlights' ? 'Highlights' : 'On-Demand'} and remove from the Live Schedule`}>
              → Move to {originEventMode === 'highlights' ? 'Highlights' : 'On-Demand'}
            </button>
            <span className="muted" style={{ fontSize: 12 }}>
              {hasBeenLive
                ? `End hands off to the next event (authoritative & sticky; confirm if the stream is still live). Move to ${originEventMode === 'highlights' ? 'Highlights' : 'On-Demand'} publishes the recording & drops it from the schedule.`
                : `End activates once this event has gone on air. Move to ${originEventMode === 'highlights' ? 'Highlights' : 'On-Demand'} publishes the recording & drops it from the schedule.`}
            </span>
          </div>
          ); })()}

          <div className="form-row">
            <label>Scheduled Start</label>
            <input
              type="datetime-local"
              value={scheduledStart}
              onChange={e => setScheduledStart(e.target.value)}
            />
          </div>

          <div className="form-row-grid">
            <div className="form-row">
              <label>Duration (minutes)</label>
              <input
                type="number"
                value={durationMinutes}
                onChange={e => setDurationMinutes(e.target.value)}
                min="1"
              />
            </div>
            <div className="form-row">
              <label>Event Type</label>
              <select value={eventType} onChange={e => setEventType(e.target.value)}>
                <option value="upcoming">Upcoming</option>
                <option value="live">Live</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>

          <div className="form-row-grid">
            <div className="form-row">
              <label>TBTV Channel</label>
              <select
                value={viloudChannelId}
                onChange={e => handleTbtvChannelSelect(e.target.value)}
              >
                <option value="">— Select Channel —</option>
                {tbtvChannels.map(ch => (
                  <option key={ch.viloud_id || ch.tbtv_channel_id} value={ch.viloud_id}>
                    {ch.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-row">
              <label>CF Live Input</label>
              <select
                value={cfLiveInputId}
                onChange={e => setCfLiveInputId(e.target.value)}
              >
                <option value="">— Select Live Input —</option>
                {liveInputs.map(li => (
                  <option key={li.uid} value={li.uid}>
                    {li.meta?.name || li.uid}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <label>Stream Notes</label>
            <textarea
              value={streamNotes}
              onChange={e => setStreamNotes(e.target.value)}
              placeholder="Internal notes..."
              rows={2}
            />
          </div>

          <div className="form-row">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={includeInSchedule}
                onChange={e => setIncludeInSchedule(e.target.checked)}
              />
              Include in Live Schedule
            </label>
          </div>

          {/* LIVE 2.0 M2b: per-event live style. Editable for any live event (not just at go-live). */}
          <div className="form-row" style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
            <label className="checkbox-label">
              <input type="checkbox" checked={autoStart} onChange={e => setAutoStart(e.target.checked)} />
              <strong>Auto-Start</strong> — go live automatically when the stream connects (within the early window)
            </label>
            <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '4px 0 0 24px' }}>
              Off → the event waits at “Starting Soon” until you press <strong>Go Live Now</strong>.
            </p>
          </div>
          <div className="form-row">
            <label className="checkbox-label">
              <input type="checkbox" checked={autoStop} onChange={e => setAutoStop(e.target.checked)} />
              <strong>Auto-Stop</strong> — end automatically shortly after the stream ends (re-stream style)
            </label>
            <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '4px 0 0 24px' }}>
              Off (truly-live) → a stream drop <em>holds</em> the event as “interrupted”; it ends only on a
              manual End (or the abandoned catch-all).
            </p>
          </div>

          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={14} /> {saving ? 'Saving...' : 'Save Live Settings'}
          </button>

          {/* YouTube Simulcast — configure/create via modal; status + manage when active (in-vader). */}
          <div className="form-row" style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12, marginTop: 12 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>YouTube Simulcast</label>
            {simMode === 'simulcast' ? (
              <div>
                {/* status / manage */}
                {(() => {
                  const yt = simStatus?.youtube || null
                  const drift = simStatus?.drift || null
                  const life = yt?.lifeCycleStatus || simStatus?.recordedStatus || null
                  const privacyNow = yt?.privacyStatus || simStatus?.recordedPrivacy || ytPrivacy
                  const lifeColors = { live: '#c0392b', complete: '#7f8c8d', created: '#2980b9', ready: '#2980b9', testing: '#e67e22', revoked: '#7f8c8d' }
                  return (
                    <>
                      <div style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span>✅ Simulcasting to YouTube</span>
                        {life && <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#fff', background: lifeColors[life] || '#555', borderRadius: 3, padding: '1px 6px' }}>{life}</span>}
                        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>({privacyNow})</span>
                        <button className="secondary" style={{ marginLeft: 'auto', fontSize: 12, padding: '2px 8px' }} disabled={simStatusLoading} onClick={refreshSimStatus}>
                          <RefreshCw size={12} style={{ verticalAlign: -2 }} /> {simStatusLoading ? 'Refreshing…' : 'Refresh'}
                        </button>
                      </div>
                      <div style={{ fontSize: 12, marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {(simStatus?.watchUrl || simWatchUrl) && <a href={simStatus?.watchUrl || simWatchUrl} target="_blank" rel="noopener noreferrer">Watch on YouTube ↗</a>}
                        {simStatus?.studioUrl && <a href={simStatus.studioUrl} target="_blank" rel="noopener noreferrer">Open in YouTube Studio ↗</a>}
                      </div>
                      {(drift?.privacy || drift?.title) && (
                        <div style={{ fontSize: 11, color: '#e67e22', marginTop: 6 }}>
                          ⚠ Drift: {drift.privacy ? 'privacy' : ''}{drift.privacy && drift.title ? ' + ' : ''}{drift.title ? 'title' : ''} differ from our record.
                        </div>
                      )}
                      {simStatus?.unlistReplay && (
                        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
                          Replay auto-unlist: {simStatus.unlistedApplied ? 'applied' : 'pending (applies once complete)'}.
                        </div>
                      )}
                      {simStatus?.ytError && <div style={{ fontSize: 11, color: 'var(--color-danger, #c0392b)', marginTop: 4 }}>{simStatus.ytError}</div>}
                    </>
                  )
                })()}
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <button className="btn-secondary" onClick={() => { setSimModalMode('edit'); setShowSimModal(true) }} title="Edit this broadcast's title, description, visibility, thumbnail and playlists in place">
                    <Radio size={14} style={{ verticalAlign: -2 }} /> Edit Simulcast
                  </button>
                  <button className="btn-secondary" disabled={controlling === 'simsync'} onClick={handleSyncSimulcast} title="Push this event's current title, start time and description to the YouTube broadcast">
                    <RotateCw size={14} style={{ verticalAlign: -2 }} /> {controlling === 'simsync' ? 'Syncing…' : 'Sync to YouTube'}
                  </button>
                  <button className="btn-danger" disabled={controlling === 'simulcast'} onClick={handleDisableSimulcast}>
                    {controlling === 'simulcast' ? 'Working…' : 'Remove Simulcast'}
                  </button>
                </div>
              </div>
            ) : item.simulcast_partner_locked ? (
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                🔒 This event is linked to a <strong>partner YouTube channel</strong>{item.yt_source_channel_id ? ` (${item.yt_source_channel_id})` : ''}. Simulcast targets <strong>TBTV on YouTube</strong> only, so it is not available here.
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn-primary" disabled={!cfLiveInputId} onClick={() => { setSimModalMode('create'); setShowSimModal(true) }}>
                  <Radio size={14} style={{ verticalAlign: -2 }} /> Configure YouTube Simulcast
                </button>
                {!cfLiveInputId && <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Assign a CF live input first.</span>}
              </div>
            )}
            {!item.simulcast_partner_locked && (
              <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '6px 0 0' }}>
                Creates a YouTube live broadcast bound to this input’s YouTube restream (auto-starts with the feed; ends on manual End). Configure thumbnail, playlists, visibility and replay handling in the dialog.
              </p>
            )}
          </div>
        </div>
      )}

      {showSimModal && (
        <SimulcastConfigModal
          item={item}
          cfLiveInputId={cfLiveInputId}
          thumbnailUrl={thumbnailUrl}
          mode={simModalMode}
          current={simModalMode === 'edit' ? simStatus : null}
          onClose={() => setShowSimModal(false)}
          onProvisioned={handleSimulcastCreated}
        />
      )}

      <div className="detail-section">
        <h3><Tag size={16} /> Tags</h3>
        <div className="tags-input-wrap">
          <div className="tags-chip-list">
            {tags.map(tag => (
              <span key={tag} className="tag-chip">
                {tag}
                <button className="tag-remove" onClick={() => removeTag(tag)}><X size={12} /></button>
              </span>
            ))}
            <input
              type="text"
              className="tag-text-input"
              value={tagInput}
              onChange={e => { setTagInput(e.target.value); setShowTagSuggestions(true) }}
              onKeyDown={handleTagKeyDown}
              onFocus={() => setShowTagSuggestions(true)}
              onBlur={() => setTimeout(() => setShowTagSuggestions(false), 150)}
              placeholder={tags.length === 0 ? 'Add tags...' : ''}
            />
          </div>
          {showTagSuggestions && filteredSuggestions.length > 0 && (
            <div className="tag-suggestions">
              {filteredSuggestions.map(s => (
                <div key={s} className="tag-suggestion" onMouseDown={() => addTag(s)}>{s}</div>
              ))}
            </div>
          )}
        </div>
        <button onClick={handleSaveTags} disabled={savingTags} style={{ marginTop: 8 }}>
          {savingTags ? 'Saving...' : 'Save Tags'}
        </button>
      </div>
    </>
  )

  return (
    <div className="catalog-detail">
      <div className="detail-header">
        <div className="detail-header-title">
          <h2>{title || 'Untitled'}</h2>
          {eventMode === 'live' && (
            <span className={`event-mode-badge live ${eventType}`}>
              <Radio size={12} /> {eventType === 'live' ? 'LIVE NOW' : eventType === 'upcoming' ? 'UPCOMING' : 'COMPLETED'}
            </span>
          )}
          {eventMode === 'ondemand' && (
            <span className="event-mode-badge ondemand">
              <Play size={12} /> ON-DEMAND
            </span>
          )}
          {eventMode === 'highlights' && (
            <span className="event-mode-badge highlights"
              style={{ background: '#facc15', color: '#000', padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Film size={12} /> HIGHLIGHTS
            </span>
          )}
        </div>
        <div className="detail-header-actions">
          {['ondemand', 'highlights', 'live'].includes(eventMode) && (() => {
            // Live → return to origin (Highlights or On-Demand); non-live → To Live.
            const returnLabel = originEventMode === 'highlights' ? 'To Highlights' : 'To On-Demand'
            return (
              <button
                className="btn-switch-mode"
                onClick={handleSwitchMode}
                disabled={switchingMode}
                title={eventMode === 'live' ? `Switch to ${originEventMode === 'highlights' ? 'Highlights' : 'On-Demand'}` : 'Switch to Live'}
              >
                <ArrowRightLeft size={14} />
                {switchingMode ? 'Switching...' : eventMode === 'live' ? returnLabel : 'To Live'}
              </button>
            )
          })()}
          <button className={`status-toggle ${status}`} onClick={handleToggleStatus} disabled={saving}>
            {status === 'active' ? <Eye size={14} /> : <EyeOff size={14} />}
            {status === 'active' ? 'Active' : 'Draft'}
          </button>
          <button className="btn-secondary" onClick={handleDuplicate} disabled={duplicating} title="Create a draft copy of this item">
            <CopyPlus size={14} /> {duplicating ? 'Duplicating...' : 'Duplicate'}
          </button>
          <button className="btn-danger" onClick={() => onDelete(item.tbtv_id)}>
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>

      {/* Share Link */}
      <div className="detail-share-link">
        <Link2 size={14} />
        <code>{getShareUrl()}</code>
        <button className="btn-copy-link" onClick={copyShareLink}>
          <Copy size={12} /> {copiedLink ? 'Copied!' : 'Copy'}
        </button>
      </div>

      {/* Sport Profile — compact row (Phase 5.1 #2). Drives which sport-profile
          prompts + vocab the AI pipeline uses for this video. Hidden entirely
          when only one profile is enabled (usually hockey), matching the
          Highlights header rule — until football (or another sport) is also
          enabled, the whole selector is noise. Has its own inline Save button
          so an editor can override the profile without touching other fields
          (the main "Save Changes" button also picks up profile_key). */}
      {availableProfiles.length > 1 && (() => {
        const dirty = profileKey !== savedProfileKey
        return (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', marginBottom: 12,
            background: 'var(--bg-secondary, #1a1a1a)',
            border: `1px solid ${dirty ? '#facc15' : '#2a2a2a'}`,
            borderRadius: 4, fontSize: 12,
          }}>
            <span style={{ color: 'var(--text-muted, #888)' }}>Sport profile:</span>
            <select
              value={profileKey}
              onChange={e => setProfileKey(e.target.value)}
              disabled={savingProfile}
              style={{
                background: 'var(--bg-primary, #0f0f0f)', color: 'var(--text-primary, #fff)',
                border: '1px solid #333', borderRadius: 4, padding: '3px 8px', fontSize: 12,
                cursor: savingProfile ? 'wait' : 'pointer',
              }}
              title="Overrides the AI dispatch for THIS video. Save to apply. Next AI re-run uses the new profile."
            >
              {/* Ensure the current key is always selectable even if it's disabled server-side. */}
              {!availableProfiles.some(p => p.profile_key === profileKey) && (
                <option value={profileKey}>{profileKey} (current)</option>
              )}
              {availableProfiles.map(p => (
                <option key={p.profile_key} value={p.profile_key}>{p.display_name}</option>
              ))}
            </select>
            <span
              style={{
                padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                background: profileKeySource === 'manual' ? '#facc15' : '#334155',
                color: profileKeySource === 'manual' ? '#000' : '#94a3b8',
              }}
              title={profileKeySource === 'manual'
                ? 'Editor explicitly picked this profile'
                : 'Assigned automatically by the detector or the DEFAULT on ingest'}
            >
              {profileKeySource === 'manual' ? 'MANUAL' : 'AUTO'}
            </span>
            {dirty && (
              <button
                type="button"
                onClick={handleSaveProfile}
                disabled={savingProfile}
                style={{
                  padding: '4px 10px', background: '#3b82f6', color: 'white',
                  border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600,
                  cursor: savingProfile ? 'wait' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}
                title="Save this profile change to the video"
              >
                <Save size={11} /> {savingProfile ? 'Saving…' : 'Save profile'}
              </button>
            )}
            <span style={{ color: 'var(--text-muted, #666)', fontSize: 11, marginLeft: 'auto' }}>
              {dirty
                ? 'Unsaved change — click Save profile (or the main Save Changes button below).'
                : 'To re-run AI with a new profile: OnDemand Library → Highlights → Settings → Clear AI / Re-run.'}
            </span>
          </div>
        )
      })()}

      {layout === 'split' ? (
        <div className="detail-split">
          <div className="detail-split-left">{playerBlock}</div>
          <div className="detail-split-right">{editBlock}</div>
        </div>
      ) : (
        <div className="detail-content">
          {playerBlock}
          {editBlock}
        </div>
      )}

      {/* Move to On-Demand Modal — post-end publish: ondemand + active + tags, drops from Live Schedule */}
      {showToVodModal && (
        <div className="media-picker-overlay" onClick={() => setShowToVodModal(false)}>
          <div className="media-picker-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="media-picker-modal-header">
              <h2><Play size={16} /> Move to {originEventMode === 'highlights' ? 'Highlights' : 'On-Demand'}</h2>
              <button onClick={() => setShowToVodModal(false)}><X size={16} /></button>
            </div>
            <div style={{ padding: 16, display: 'grid', gap: 12 }}>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)' }}>
                Publishes this event's recording as {originEventMode === 'highlights' ? 'a highlight' : 'on-demand'} and <strong>removes it from the Live Schedule</strong>.
              </p>
              <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={toVodActive} onChange={e => setToVodActive(e.target.checked)} />
                Set status to <strong>Active</strong> (publish now)
              </label>
              <div className="form-row">
                <label>Tags (comma-separated)</label>
                <input type="text" value={toVodTags} onChange={e => setToVodTags(e.target.value)} placeholder="e.g. PIHL, Hockey, 2026" />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                <button className="btn-secondary" onClick={() => setShowToVodModal(false)}>Not now</button>
                <button className="btn-primary" disabled={movingToVod} onClick={handleMoveToVod}>
                  <Play size={14} /> {movingToVod ? 'Moving…' : `Move to ${originEventMode === 'highlights' ? 'Highlights' : 'On-Demand'}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Go Live Settings Modal — collect the required live settings before switching to live mode */}
      {showGoLiveModal && (
        <div className="media-picker-overlay" onClick={() => setShowGoLiveModal(false)}>
          <div className="media-picker-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="media-picker-modal-header">
              <h2><Radio size={16} /> Go Live — Event Settings</h2>
              <button onClick={() => setShowGoLiveModal(false)}><X size={16} /></button>
            </div>
            <div style={{ padding: 16, display: 'grid', gap: 12 }}>
              <div className="form-row">
                <label>Scheduled Start <span style={{ color: 'var(--color-highlight)' }}>*</span></label>
                <input type="datetime-local" value={scheduledStart} onChange={e => setScheduledStart(e.target.value)} />
              </div>
              <div className="form-row-grid">
                <div className="form-row">
                  <label>Duration (minutes)</label>
                  <input type="number" min="1" value={durationMinutes} onChange={e => setDurationMinutes(e.target.value)} />
                </div>
                <div className="form-row">
                  <label>Event Type</label>
                  <select value={eventType} onChange={e => setEventType(e.target.value)}>
                    <option value="upcoming">Upcoming</option>
                    <option value="live">Live</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <label>TBTV Channel</label>
                <select value={viloudChannelId} onChange={e => handleTbtvChannelSelect(e.target.value)}>
                  <option value="">— Select Channel —</option>
                  {tbtvChannels.map(ch => (
                    <option key={ch.viloud_id || ch.tbtv_channel_id} value={ch.viloud_id}>{ch.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label>CF Live Input <span style={{ color: 'var(--color-highlight)' }}>*</span></label>
                <select value={cfLiveInputId} onChange={e => setCfLiveInputId(e.target.value)}>
                  <option value="">— Select Live Input —</option>
                  {liveInputs.map(li => (
                    <option key={li.uid} value={li.uid}>{li.meta?.name || li.uid}</option>
                  ))}
                </select>
              </div>
              <div className="form-row" style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={autoStart} onChange={e => setAutoStart(e.target.checked)} style={{ width: 'auto' }} />
                  <span><strong>Auto-Start</strong> — go live automatically when the stream connects (within the early window)</span>
                </label>
                <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '4px 0 0 24px' }}>
                  Off → the event waits at “Starting Soon” until you press <strong>Go Live Now</strong>.
                </p>
              </div>
              <div className="form-row">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={autoStop} onChange={e => setAutoStop(e.target.checked)} style={{ width: 'auto' }} />
                  <span><strong>Auto-Stop</strong> — end automatically shortly after the stream ends (re-stream style)</span>
                </label>
                <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '4px 0 0 24px' }}>
                  Off (truly-live) → a stream drop <em>holds</em> the event as “interrupted”; it ends only on a manual
                  End (or the abandoned catch-all).
                </p>
              </div>
              <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: 0 }}>
                <span style={{ color: 'var(--color-highlight)' }}>*</span> required. The event moves to the Live schedule
                and will stream from the selected CF live input. Re-stream its HLS via <strong>Live Stream → Streamer</strong>.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                <button className="btn-secondary" onClick={() => setShowGoLiveModal(false)}>Cancel</button>
                <button className="btn-primary" disabled={switchingMode || !scheduledStart || !cfLiveInputId} onClick={() => doSwitchMode('live')}>
                  <Radio size={14} /> {switchingMode ? 'Switching…' : 'Go Live'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mux Picker Modal */}
      {showMuxPicker && (
        <div className="media-picker-overlay" onClick={() => setShowMuxPicker(false)}>
          <div className="media-picker-modal" onClick={e => e.stopPropagation()}>
            <div className="media-picker-modal-header">
              <h2><Play size={16} /> Select Mux Asset</h2>
              <button onClick={() => setShowMuxPicker(false)}><X size={16} /></button>
            </div>
            <div className="media-picker-toolbar">
              <input
                type="text"
                placeholder="Search by title…"
                value={muxSearch}
                onChange={e => setMuxSearch(e.target.value)}
                autoFocus
              />
              <select value={muxTagFilter} onChange={e => setMuxTagFilter(e.target.value)}>
                <option value="">All Tags</option>
                {(allTags || []).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {muxLoading ? (
              <div className="media-picker-empty">Loading Mux assets…</div>
            ) : muxAssets.length === 0 ? (
              <div className="media-picker-empty">No Mux assets found</div>
            ) : (() => {
              const filtered = muxAssets.filter(a => {
                const t = (a.meta?.title || a.passthrough || a.id).toLowerCase()
                if (muxSearch && !t.includes(muxSearch.toLowerCase())) return false
                if (muxTaggedIds) {
                  const tbtvId = a.meta?.external_id
                  const archiveKey = a.passthrough
                  if (!(tbtvId && muxTaggedIds.tbtvIds.has(tbtvId)) && !(archiveKey && muxTaggedIds.archiveKeys.has(archiveKey))) return false
                }
                return true
              })
              return filtered.length === 0 ? (
                <div className="media-picker-empty">No matches</div>
              ) : (
                <div className="media-picker-modal-list">
                  {filtered.map(a => {
                    const pid = a.playback_ids?.[0]?.id
                    const muxHls = pid ? `https://stream.mux.com/${pid}.m3u8` : null
                    const muxTitle = a.meta?.title || a.passthrough || a.id
                    const isLinked = mediaUrl === muxHls
                    const assetTags = a.meta?.external_id ? (muxAssetTags[a.meta.external_id] || []) : []
                    return (
                      <div
                        key={a.id}
                        className={`media-picker-item${isLinked ? ' linked' : ''}`}
                        onClick={() => {
                          if (!muxHls) return
                          setMediaUrl(muxHls)
                          setVideoType('MUX')
                          if (a.duration) setDuration(Math.floor(a.duration))
                          setShowMuxPicker(false)
                        }}
                      >
                        {pid && <img src={`https://image.mux.com/${pid}/thumbnail.jpg?width=80&height=45&time=5`} alt="" />}
                        <div className="media-picker-item-info">
                          <div className="media-picker-item-title">{muxTitle}</div>
                          <div className="media-picker-item-meta">{a.id.slice(0, 16)}… · {a.status}{a.duration > 0 ? ` · ${Math.floor(a.duration / 60)}m${Math.floor(a.duration % 60)}s` : ''}</div>
                          {assetTags.length > 0 && (
                            <div className="media-picker-item-tags">
                              {assetTags.slice(0, 3).map(t => <span key={t} className="media-picker-tag">{t}</span>)}
                              {assetTags.length > 3 && <span className="media-picker-tag">+{assetTags.length - 3}</span>}
                            </div>
                          )}
                        </div>
                        {isLinked ? (
                          <span className="media-picker-item-action linked">Linked</span>
                        ) : muxHls ? (
                          <span className="media-picker-item-action use">Use</span>
                        ) : null}
                      </div>
                    )
                  })}
                </div>
              )
            })()}
          </div>
        </div>
      )}

      {/* CF Stream Picker Modal */}
      {showCfPicker && (
        <div className="media-picker-overlay" onClick={() => setShowCfPicker(false)}>
          <div className="media-picker-modal" onClick={e => e.stopPropagation()}>
            <div className="media-picker-modal-header">
              <h2><Play size={16} /> Select CF Stream Video</h2>
              <button onClick={() => setShowCfPicker(false)}><X size={16} /></button>
            </div>
            <div className="media-picker-toolbar">
              <input
                type="text"
                placeholder="Search by title…"
                value={cfSearch}
                onChange={e => setCfSearch(e.target.value)}
                autoFocus
              />
              <select value={cfTagFilter} onChange={e => setCfTagFilter(e.target.value)}>
                <option value="">All Tags</option>
                {(allTags || []).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {cfLoading ? (
              <div className="media-picker-empty">Loading CF Stream videos…</div>
            ) : cfStreams.length === 0 ? (
              <div className="media-picker-empty">No CF Stream videos found</div>
            ) : (() => {
              const filtered = cfStreams.filter(s => {
                const t = (s.meta?.name || s.uid).toLowerCase()
                if (cfSearch && !t.includes(cfSearch.toLowerCase())) return false
                if (cfTaggedUids && !cfTaggedUids.has(s.uid)) return false
                return true
              })
              return filtered.length === 0 ? (
                <div className="media-picker-empty">No matches</div>
              ) : (
                <div className="media-picker-modal-list">
                  {filtered.map(s => {
                    const cfHls = s.playback?.hls || (s.uid ? `https://customer-bs9kri9klnfqt57u.cloudflarestream.com/${s.uid}/manifest/video.m3u8` : null)
                    const cfTitle = s.meta?.name || s.uid
                    const cfThumb = s.thumbnail || (s.uid ? `https://customer-bs9kri9klnfqt57u.cloudflarestream.com/${s.uid}/thumbnails/thumbnail.jpg?width=120&height=68&time=5s` : null)
                    const isLinked = mediaUrl === cfHls
                    const streamTags = cfStreamTags[s.uid] || []
                    return (
                      <div
                        key={s.uid}
                        className={`media-picker-item${isLinked ? ' linked' : ''}`}
                        onClick={() => {
                          if (!cfHls) return
                          setMediaUrl(cfHls)
                          setVideoType('CF')
                          if (s.duration) setDuration(Math.floor(s.duration))
                          setShowCfPicker(false)
                        }}
                      >
                        {cfThumb && <img src={cfThumb} alt="" />}
                        <div className="media-picker-item-info">
                          <div className="media-picker-item-title">{cfTitle}</div>
                          <div className="media-picker-item-meta">{s.uid.slice(0, 16)}…{s.duration > 0 ? ` · ${Math.floor(s.duration / 60)}m${Math.floor(s.duration % 60)}s` : ''}</div>
                          {streamTags.length > 0 && (
                            <div className="media-picker-item-tags">
                              {streamTags.slice(0, 3).map(t => <span key={t} className="media-picker-tag">{t}</span>)}
                              {streamTags.length > 3 && <span className="media-picker-tag">+{streamTags.length - 3}</span>}
                            </div>
                          )}
                        </div>
                        {isLinked ? (
                          <span className="media-picker-item-action linked">Linked</span>
                        ) : cfHls ? (
                          <span className="media-picker-item-action use">Use</span>
                        ) : null}
                      </div>
                    )
                  })}
                </div>
              )
            })()}
          </div>
        </div>
      )}
    </div>
  )
}

// Step 32: Linked-reel section for content_type='highlight' rows.
// Shows the currently-linked reel, lets the editor swap to a different
// available reel, or unlink entirely (which leaves the catalog row but
// breaks playback until a new reel is linked).
//
// Available reels = status='ready' AND (tbtv_id IS NULL OR tbtv_id =
// this row's tbtv_id). Backend enforces the constraint at swap time
// (409 on conflict) so the picker is best-effort filtered.
//
// The swap PATCHes /api/vod/:tbtv_id with { reel_id: <new> }. The
// extended handler in _worker.js runs the atomic swap (PATCH old reel
// .tbtv_id=null, PATCH new reel .tbtv_id=this), recomputes media_url +
// duration + thumbnail from the new reel, and saves vod_catalog.
function HighlightLinkedReelSection({ tbtvId, api, onRelinked }) {
  const [reels, setReels] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadErr, setLoadErr] = useState(null)
  const [showPicker, setShowPicker] = useState(false)
  const [selectedReelId, setSelectedReelId] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitErr, setSubmitErr] = useState(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setLoadErr(null)
    try {
      const r = await api('/highlights/reels?status=ready,preparing&limit=200')
      setReels(r.reels || [])
    } catch (e) {
      setLoadErr(e.message)
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => { refresh() }, [refresh])

  const currentReel = reels.find(r => r.tbtv_id === tbtvId)
  const availableReels = reels.filter(r => !r.tbtv_id || r.tbtv_id === tbtvId)

  async function submit() {
    if (!selectedReelId || selectedReelId === currentReel?.reel_id) return
    setSubmitting(true)
    setSubmitErr(null)
    try {
      await api(`/vod/${tbtvId}`, {
        method: 'PATCH',
        body: JSON.stringify({ reel_id: selectedReelId }),
      })
      setShowPicker(false)
      await refresh()
      if (onRelinked) onRelinked()
    } catch (e) {
      setSubmitErr(e.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="detail-section" style={{ borderLeft: '3px solid #facc15', paddingLeft: 14 }}>
      <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        🎬 Linked Reel
        <span style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>(Highlights content)</span>
      </h3>
      {loading && <div style={{ color: '#888', fontSize: 13 }}>Loading reels…</div>}
      {loadErr && <div style={{ color: '#dc2626', fontSize: 13 }}>Failed to load: {loadErr}</div>}
      {!loading && !loadErr && (
        <>
          {currentReel ? (
            <div style={{ background: '#1a1a1a', padding: 10, borderRadius: 4, fontSize: 13 }}>
              <div style={{ fontWeight: 500 }}>{currentReel.title || '(untitled reel)'}</div>
              <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#888', marginTop: 4 }}>
                {currentReel.reel_id} · status: {currentReel.status}
              </div>
            </div>
          ) : (
            <div style={{ color: '#facc15', fontSize: 13, padding: 8, background: '#451a03', borderRadius: 4 }}>
              ⚠ No reel currently linked. Playback will fail until a reel is linked.
            </div>
          )}
          <div style={{ marginTop: 10 }}>
            {!showPicker ? (
              <button onClick={() => { setShowPicker(true); setSelectedReelId(currentReel?.reel_id || '') }}
                style={{ padding: '6px 12px', background: 'transparent', border: '1px solid #facc15', color: '#facc15', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                {currentReel ? '🔄 Change Linked Reel' : '🔗 Link a Reel'}
              </button>
            ) : (
              <div style={{ background: '#1a1a1a', padding: 12, borderRadius: 4 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#888', marginBottom: 6 }}>
                  Select reel ({availableReels.length} available)
                </label>
                <select value={selectedReelId} onChange={e => setSelectedReelId(e.target.value)}
                  style={{ width: '100%', padding: '6px 8px', background: '#0d0d0d', border: '1px solid #333', borderRadius: 4, color: '#ddd', fontSize: 13 }}>
                  <option value="">— pick a reel —</option>
                  {availableReels.map(r => (
                    <option key={r.reel_id} value={r.reel_id}>
                      {r.tbtv_id === tbtvId ? '✓ ' : ''}{r.title || '(untitled)'} · {r.reel_id}
                    </option>
                  ))}
                </select>
                {submitErr && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{submitErr}</div>}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 8 }}>
                  <button onClick={() => { setShowPicker(false); setSubmitErr(null) }} disabled={submitting}
                    style={{ padding: '6px 10px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: '#ddd', fontSize: 12, cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button onClick={submit}
                    disabled={submitting || !selectedReelId || selectedReelId === currentReel?.reel_id}
                    style={{ padding: '6px 12px', background: '#facc15', border: 'none', borderRadius: 4, color: '#000', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                    {submitting ? 'Saving…' : 'Save (atomic swap)'}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: '#666', marginTop: 8 }}>
                  Atomic swap: old reel unlinks, new reel links, vod_catalog.media_url +
                  duration + thumbnail refresh from new reel. tbtv_id is stable.
                  Other fields (title, descriptions, tags, release date) stay as you've set them.
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
